import {
  Box, Chip, FormControl, FormControlLabel, FormHelperText, FormLabel, MenuItem, Radio, RadioGroup, TextField,
} from '@mui/material';
import type { ReactNode } from 'react';
import { textFieldProps } from '../textFieldProps';
import type { FieldComponentProps, FieldOption, FieldTypeSpec, ResolvedField } from '../types';

type OptionValue = FieldOption['value'];

function labelOf(field: ResolvedField<unknown>, value: unknown): ReactNode {
  return field.options?.find((o) => o.value === value)?.label ?? String(value);
}

export interface SelectConfig { emptyLabel?: string }

export function SelectInput({ field, value, onChange, onBlur, error }: FieldComponentProps<OptionValue | null, SelectConfig>) {
  const { emptyLabel } = field.config;
  return (
    <TextField {...textFieldProps(field, error)} select value={value ?? ''} onBlur={onBlur}
               onChange={(e) => {
                 const picked = field.options?.find((o) => String(o.value) === e.target.value);
                 onChange(picked ? picked.value : null);
               }}>
      {emptyLabel != null && <MenuItem value=""><em>{emptyLabel}</em></MenuItem>}
      {(field.options ?? []).map((o) => (
        <MenuItem key={String(o.value)} value={String(o.value)} disabled={o.disabled}>{o.label}</MenuItem>
      ))}
    </TextField>
  );
}

export function MultiSelectInput({ field, value, onChange, onBlur, error }: FieldComponentProps<OptionValue[] | null>) {
  const selected = value ?? [];
  const base = textFieldProps(field, error);
  return (
    <TextField {...base} select value={selected.map(String)} onBlur={onBlur}
               onChange={(e) => {
                 const keys = e.target.value as unknown as string[];
                 onChange((field.options ?? []).filter((o) => keys.includes(String(o.value))).map((o) => o.value));
               }}
               slotProps={{
                 ...base.slotProps,
                 select: {
                   multiple: true,
                   renderValue: () => (
                     <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                       {selected.map((v) => <Chip key={String(v)} size="small" label={labelOf(field, v)} />)}
                     </Box>
                   ),
                 },
               }}>
      {(field.options ?? []).map((o) => (
        <MenuItem key={String(o.value)} value={String(o.value)} disabled={o.disabled}>{o.label}</MenuItem>
      ))}
    </TextField>
  );
}

export interface RadioConfig { row?: boolean }

export function RadioInput({ field, value, onChange, error }: FieldComponentProps<OptionValue | null, RadioConfig>) {
  return (
    <FormControl error={!!error} disabled={field.disabled} required={field.required} className={field.className}
                 sx={field.sx} {...field.props}>
      {field.label && <FormLabel>{field.label}</FormLabel>}
      <RadioGroup row={field.config.row ?? true} value={value == null ? '' : String(value)}
                  onChange={(e) => onChange(field.options?.find((o) => String(o.value) === e.target.value)?.value ?? null)}>
        {(field.options ?? []).map((o) => (
          <FormControlLabel key={String(o.value)} value={String(o.value)} disabled={o.disabled}
                            control={<Radio size={field.size} inputProps={field.attrs} />} label={o.label} />
        ))}
      </RadioGroup>
      {(error ?? field.helperText) && <FormHelperText>{error ?? field.helperText}</FormHelperText>}
    </FormControl>
  );
}

export const choiceTypes: Record<string, FieldTypeSpec> = {
  select: { component: SelectInput, display: (v, f) => labelOf(f, v) },
  multiselect: {
    component: MultiSelectInput,
    display: (v, f) => (v as OptionValue[]).map((x) => labelOf(f, x)).join(', '),
  },
  radio: { component: RadioInput, display: (v, f) => labelOf(f, v) },
};
